import firebase from "firebase/app";
import { AnyDefinition, DatabaseNode, InferType, toSchema } from "@lifeat/shared/data-models/definition";
import { firestore } from "@firebase-local/firebase";
import { logError } from "./ConsoleLoggerService";

import Query = firebase.firestore.Query;
import WhereFilterOp = firebase.firestore.WhereFilterOp;

export type QueryOptions = {
    filters?: {
        fieldPath: string;
        opStr: WhereFilterOp;
        value: any;
    }[];
    orderBy?: {
        key: string;
        direction: "asc" | "desc";
    };
    limit?: number;
};
type SubscriptionCallbackPayload<T> = { success: false; error: unknown } | { success: true; value: T };
type Unsubscribe = () => void;
type SubscriptionCallback<T> = (payload: SubscriptionCallbackPayload<T>) => void;
/**
 * Adapter service for Cloud Firestore database.
 *
 * Prefer to use this service over directly using the firebase package.
 * The methods of this service are aware of the structure of LifeAt database
 * and returns correct type.
 */
export function FirestoreService() {
    return {
        /**
         * Read a single document from the database.
         * @param node Document node to read.
         * @returns the node value or `null` if the node does not exist on database.
         */
        getDocument: async <TDefinition extends AnyDefinition, TPath extends string>(
            node: DatabaseNode<TDefinition, TPath>,
        ): Promise<InferType<TDefinition> | null | undefined> => {
            const schema = toSchema(node.definition);
            const ref = firestore.doc(node.path);
            const snapshot = await ref.get();
            if (!snapshot.exists) {
                return null;
            }
            const raw = snapshot.data();
            const parsed = schema.safeParse(raw);
            if (parsed.success === false) {
                logError(parsed.error);
                throw new Error("Failed to parse document data from Firestore");
            }
            return parsed.data;
        },
        getDocumentListener: <TDefinition extends AnyDefinition, TPath extends string>(
            node: DatabaseNode<TDefinition, TPath>,
            callback: SubscriptionCallback<InferType<TDefinition> | null | undefined>,
        ): Unsubscribe => {
            const schema = toSchema(node.definition);
            const ref = firestore.doc(node.path);
            return ref.onSnapshot(
                (snapshot) => {
                    if (snapshot.exists) {
                        const raw = snapshot.data();
                        const parsed = schema.safeParse(raw);
                        if (parsed.success === false) {
                            logError(parsed.error);
                            callback({ success: false, error: parsed.error });
                        } else {
                            callback({ success: true, value: parsed.data });
                        }
                    } else {
                        callback({ success: true, value: null });
                    }
                },
                (error) => {
                    logError(error);
                    callback({ success: false, error });
                },
            );
        },
        /**
         * Read documents from a collection.
         * @param node Collection node to read documents from.
         * @returns the node value or `null` if the node does not exist on database.
         */
        getCollection: async <TDefinition extends AnyDefinition, TPath extends string>(
            node: DatabaseNode<TDefinition, TPath>,
        ): Promise<InferType<TDefinition> | null | undefined> => {
            const schema = toSchema(node.definition);
            const ref = firestore.collection(node.path);
            const snapshot = await ref.get();
            if (snapshot.empty) {
                return null;
            }
            const collection: { [id: string]: firebase.firestore.DocumentData } = {};
            snapshot.docs.forEach((doc) => {
                collection[doc.id] = doc.data();
            });
            const parsed = schema.safeParse(collection);
            if (parsed.success === false) {
                logError(parsed.error);
                throw new Error("Failed to parse collection data from Firestore");
            }
            return parsed.data;
        },

        getCollectionListener: <TDefinition extends AnyDefinition, TPath extends string>(
            node: DatabaseNode<TDefinition, TPath>,
            callback: SubscriptionCallback<InferType<TDefinition> | null | undefined>,
            options?: QueryOptions,
        ): Unsubscribe => {
            const schema = toSchema(node.definition);
            let ref: Query = firestore.collection(node.path);
            if (options?.filters) {
                options.filters.forEach((filter) => {
                    ref = ref.where(filter.fieldPath, filter.opStr, filter.value);
                });
            }
            if (options?.orderBy) {
                ref = ref.orderBy(options.orderBy.key, options.orderBy.direction);
            }
            if (options?.limit) {
                ref = ref.limit(options.limit);
            }

            return ref.onSnapshot(
                (snapshot) => {
                    if (!snapshot.empty) {
                        const collection: { [id: string]: firebase.firestore.DocumentData } = {};
                        snapshot.docs.forEach((doc) => {
                            collection[doc.id] = doc.data();
                        });
                        const parsed = schema.safeParse(collection);
                        if (parsed.success === false) {
                            logError(parsed.error);
                            callback({ success: false, error: parsed.error });
                        } else {
                            callback({ success: true, value: parsed.data });
                        }
                    } else {
                        callback({ success: true, value: null });
                    }
                },
                (error) => {
                    logError(error);
                    callback({ success: false, error });
                },
            );
        },
    };
}
export type FirestoreService = ReturnType<typeof FirestoreService>;
